import React from 'react';
import BlogLayout, { Section, Para, BulletList, NumberedList, InternalLink } from './BlogLayout';

const slug = 'spaced-repetition-vocabulary-reading';

const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Why Looking Up Words While Reading Builds Permanent Vocabulary',
    description: 'Spaced repetition turns quick word lookups into long-term memory. Learn how combining reading practice with flashcard science builds vocabulary that sticks.',
    datePublished: '2026-05-09',
    author: { '@type': 'Organization', name: 'ReaderFlex' },
    publisher: { '@type': 'Organization', name: 'ReaderFlex', url: 'https://readerflex.com' },
    url: `https://readerflex.com/blog/${slug}`,
};

const SpacedRepetition = () => (
    <BlogLayout
        slug={slug}
        title="Build Permanent Vocabulary by Looking Up Words"
        description="Spaced repetition turns quick word lookups into long-term memory. Combine reading with flashcard science to build vocabulary that sticks."
        date="May 9, 2026"
        readMin={6}
        jsonLd={jsonLd}
    >
        <Section title="The Vocabulary Problem">
            <Para>
                You look up a word. You read the definition. You nod. Thirty minutes later, you have forgotten it. This happens to everyone, and it is not a memory problem. It is a learning strategy problem.
            </Para>
            <Para>
                The human brain is not designed to retain information from a single exposure. Hermann Ebbinghaus demonstrated this in 1885 with his "forgetting curve": without reinforcement, you lose about 70% of newly learned information within 24 hours and over 90% within a week. Looking up a word once is almost the same as not looking it up at all.
            </Para>
        </Section>

        <Section title="What is Spaced Repetition?">
            <Para>
                Spaced repetition is a study technique where you review information at increasing intervals. Instead of cramming a word 10 times in one day, you see it once today, once tomorrow, once in 3 days, once in a week, once in two weeks, and so on. Each successful recall strengthens the memory and extends the interval.
            </Para>
            <Para>
                The technique exploits a well-documented phenomenon called the spacing effect: memories are stronger when study sessions are spread out over time rather than concentrated. Decades of research in cognitive science have confirmed that spaced repetition is one of the most effective learning methods known.
            </Para>
        </Section>

        <Section title="Why Context Matters for Vocabulary">
            <Para>
                Looking up a word in a dictionary gives you a definition. Encountering that word while reading gives you context: how it fits in a sentence, what words surround it, what tone it carries, what situation it describes. Context is what makes vocabulary stick.
            </Para>
            <Para>
                Research by Nagy, Herman, and Anderson found that children who encounter new words in context while reading acquire vocabulary 10x more effectively than those who study word lists. The same principle applies to adults, especially when reading in a foreign language.
            </Para>
            <Para>
                This is why looking up words during a reading session is so much more effective than studying flashcards in isolation. The reading provides rich context. The spaced repetition provides the reinforcement. Together, they produce vocabulary retention that neither approach achieves alone.
            </Para>
        </Section>

        <Section title="How ReaderFlex Combines Reading and Vocabulary">
            <Para>
                ReaderFlex integrates vocabulary building directly into the reading experience:
            </Para>
            <NumberedList>
                <li><strong>Pronunciation guide:</strong> When you generate an article, ReaderFlex identifies 5 to 10 difficult words and provides phonetic pronunciation and definitions. These appear alongside the article so you can reference them without leaving the page.</li>
                <li><strong>Vocabulary bank:</strong> Every word you look up is saved to your personal vocabulary bank with its definition, phonetic pronunciation, the language it came from, and how many times you have looked it up.</li>
                <li><strong>Flashcard system:</strong> Words in your bank are automatically scheduled for spaced repetition review. The system tracks your confidence level (0 to 3) for each word and adjusts the review interval accordingly.</li>
                <li><strong>Progress tracking:</strong> Your vocabulary stats show total words saved, words mastered, languages studied, and flashcard session counts. Badges reward milestones like saving 25 words, studying 50 flashcards, or mastering 50 words.</li>
            </NumberedList>
        </Section>

        <Section title="The Confidence Levels">
            <Para>
                ReaderFlex uses a four-level confidence system for spaced repetition:
            </Para>
            <BulletList>
                <li><strong>Level 0 (New):</strong> You just saved the word. First review is scheduled for tomorrow.</li>
                <li><strong>Level 1 (Learning):</strong> You recognized the word once. Next review in 3 days.</li>
                <li><strong>Level 2 (Familiar):</strong> You have recalled it correctly multiple times. Next review in 7 days.</li>
                <li><strong>Level 3 (Mastered):</strong> The word is in your long-term memory. Review interval extends to 14+ days.</li>
            </BulletList>
            <Para>
                When you get a word wrong during a flashcard session, it drops back one confidence level and the review interval shortens. This ensures that shaky words get more practice while strong words do not waste your time.
            </Para>
        </Section>

        <Section title="Vocabulary and Reading Speed Are Connected">
            <Para>
                A larger vocabulary directly improves reading speed. When you encounter familiar words, your <InternalLink to="/blog/eye-fixation-how-eyes-read">fixation duration</InternalLink> is shorter because your brain recognizes the word faster. When you encounter unfamiliar words, you slow down, possibly <InternalLink to="/blog/stop-subvocalizing-read-faster">subvocalize</InternalLink> to sound it out, and sometimes re-read the surrounding context to infer meaning.
            </Para>
            <Para>
                Studies on reading fluency show that vocabulary knowledge is one of the strongest predictors of reading speed, alongside decoding ability and working memory capacity. Building vocabulary is not separate from speed training. It is part of it.
            </Para>
            <Para>
                This is especially true when reading in foreign languages. ReaderFlex supports 16 languages, and the vocabulary bank tracks words by language. If you are learning Spanish by reading Spanish articles, every word you save and review with spaced repetition accelerates both your vocabulary and your reading speed in that language.
            </Para>
        </Section>

        <Section title="Make It a Habit">
            <Para>
                The best way to build vocabulary through reading is to make it part of your <InternalLink to="/blog/15-minute-speed-reading-routine">daily routine</InternalLink>. During each reading session, save any word you do not know or are unsure about. Do not worry about saving too many. The spaced repetition system will prioritize them for you.
            </Para>
            <Para>
                Then, once or twice a day, do a quick flashcard review session. It takes 2 to 3 minutes for 10 to 15 cards. The combination of encountering words in real reading context and reinforcing them through spaced repetition is the fastest known method for building permanent, usable vocabulary.
            </Para>
            <Para>
                Your vocabulary grows. Your reading gets faster. Your <InternalLink to="/blog/reading-speed-vs-comprehension">comprehension improves</InternalLink>. It is a virtuous cycle, and it starts with the simple act of looking up a word and saving it.
            </Para>
        </Section>
    </BlogLayout>
);

export default SpacedRepetition;
